import axiosInstance from '../config/axios';

// ──────────────────────────────────────────────
// Profile / Account Management APIs
// ──────────────────────────────────────────────

/**
 * Fetch a user's profile (defaults to authenticated user if no userId provided)
 * @param {string} [userId]
 */
export const getProfile = (userId) =>
  axiosInstance.get('/profile', { params: userId ? { userId } : {} }).then((res) => res.data);

/**
 * Update user profile (supports multipart for avatar upload)
 * @param {FormData|Object} data
 */
export const updateProfile = (data) => {
  const isFormData = data instanceof FormData;
  return axiosInstance
    .post('/update-profile', data, {
      headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : {},
    })
    .then((res) => res.data);
};

/**
 * Upload profile picture
 * @param {FormData} formData - Must contain 'image' field
 */
export const uploadProfilePicture = (formData) =>
  axiosInstance
    .post('/upload-profile-picture', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    .then((res) => res.data);

/**
 * Get available languages list
 */
export const getLanguages = () =>
  axiosInstance.get('/languages').then((res) => res.data);

/**
 * Add languages to user profile
 * @param {string[]} language_ids
 */
export const addLanguages = (language_ids) =>
  axiosInstance.post('/addLanguage', { language_ids }).then((res) => res.data);

/**
 * Get teacher earnings (teacher role only)
 */
export const getEarnings = () =>
  axiosInstance.get('/get-earnings').then((res) => res.data);

/**
 * Get student's enrolled courses
 */
export const getMyLearning = () =>
  axiosInstance.get('/my-learning').then((res) => res.data);

/**
 * Get bank details (teacher role)
 */
export const getBankDetails = () =>
  axiosInstance.get('/bank-details').then((res) => res.data);

/**
 * Save bank details (teacher role)
 * @param {Object} bankData
 */
export const saveBankDetails = (bankData) =>
  axiosInstance.post('/bank-details', bankData).then((res) => res.data);

// ──────────────────────────────────────────────
// Blue Verification Subscription APIs
// ──────────────────────────────────────────────

/**
 * Submit Blue Verification Request
 * @param {FormData} formData - Must contain 'documents' field
 */
export const requestBlueVerification = (formData) =>
  axiosInstance.post('/blue-verification/request', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then((res) => res.data);

/**
 * Get Blue Verification Status
 */
export const getBlueVerificationStatus = () =>
  axiosInstance.get('/blue-verification/status').then((res) => res.data);

/**
 * Purchase Blue Verification Subscription
 * @param {string} paymentMethodId
 */
export const purchaseBlueVerification = (paymentMethodId) =>
  axiosInstance.post('/blue-verification/purchase', { payment_method_id: paymentMethodId }).then((res) => res.data);

/**
 * Cancel Blue Verification Subscription
 */
export const cancelBlueVerification = () =>
  axiosInstance.post('/blue-verification/cancel').then((res) => res.data);

/**
 * Register User to Blue Tick (PayPal / completed transaction flow)
 * @param {string} chargeId
 */
export const registerUserToBlueTick = (chargeId) =>
  axiosInstance.post('/register-user-to-blue-tick', { charge_id: chargeId }).then((res) => res.data);

// ──────────────────────────────────────────────
// User Subscription APIs
// ──────────────────────────────────────────────

/**
 * Get subscription plans and active user subscriptions
 * @param {string} [type] - Optional plan type filter (community, class, event)
 * @param {string} [status] - Optional user subscription status filter (active, trial, cancelled, expired)
 */
export const getSubscriptionPlans = (type, status) => {
  const params = {};
  if (type) params.type = type;
  if (status) params.status = status;
  return axiosInstance.get('/subscription-plans', { params }).then((res) => res.data);
};

/**
 * Get a single subscription plan by ID
 * @param {string} id
 */
export const getSubscriptionPlanById = (id) =>
  axiosInstance.get(`/subscription-plans/${id}`).then((res) => res.data);

/**
 * Create a new user subscription
 * @param {string} planId
 * @param {string} chargeId - Paypal order / transaction reference ID
 */
export const createUserSubscription = (planId, chargeId) =>
  axiosInstance.post('/subscriptions/subscribe', {
    subscription_plan_id: planId,
    charge_id: chargeId,
  }).then((res) => res.data);

/**
 * Cancel a user subscription
 * @param {string} userId
 */
export const cancelUserSubscription = (userId) =>
  axiosInstance.post(`/admin/subscriptions/${userId}/cancel`, {
    reason: 'User cancelled',
  }).then((res) => res.data);

/**
 * Get a user subscription by ID
 * @param {string} subscriptionId
 */
export const getUserSubscriptionById = (subscriptionId) =>
  axiosInstance.get(`/subscriptions/${subscriptionId}`).then((res) => res.data);

// ──────────────────────────────────────────────
// Teacher Screening APIs
// ──────────────────────────────────────────────

/**
 * Submit Teacher Screening Questionnaire
 * @param {FormData} formData - Questionnaire fields and 'certificateFile' file
 */
export const submitTeacherScreening = (formData) =>
  axiosInstance.post('/teacher-screening', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then((res) => res.data);

/**
 * Get Teacher Questionnaire Status
 */
export const getTeacherQuestionnaireStatus = () =>
  axiosInstance.get('/teacher-questionnaire-status').then((res) => res.data);

/**
 * Get Saved Posts
 */
export const getSavedPostsApi = () =>
  axiosInstance.get('/saved-posts').then((res) => res.data);

/**
 * Toggle Save / Unsave post
 * @param {string} postId
 */
export const toggleSavePostApi = (postId) =>
  axiosInstance.post('/save-post', { post_id: postId }).then((res) => res.data);

/**
 * Get Practice History
 * @param {number} page
 * @param {number} limit
 */
export const getPracticeHistoryApi = (page = 1, limit = 10) =>
  axiosInstance.get('/get-practice-history', { params: { page, limit } }).then((res) => res.data);

/**
 * Get Payment / Transaction History
 */
export const getPaymentHistoryApi = () =>
  axiosInstance.get('/get-transaction-history').then((res) => res.data);
